import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  obtenerTodosLosNinos, obtenerNinoPorId, obtenerEvaluaciones,
  deleteEvaluacion
} from '../services/evaluacionesService';
import { Trash2, ChevronLeft, Search, User } from 'lucide-react';
import PlantillaEvaluacion from '../components/PlantillaEvaluacion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatearFechaLocal } from '../utils/dateUtils';
const GestionEvaluaciones = () => {
  const [ninos, setNinos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [ninoSeleccionado, setNinoSeleccionado] = useState(null);
  const [edad, setEdad] = useState('');
  const [grupoEdad, setGrupoEdad] = useState('');
  const [plantillaCargada, setPlantillaCargada] = useState(false);
  const [vistaActual, setVistaActual] = useState('recientes');
  const [seccionSeleccionada, setSeccionSeleccionada] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState({});
  const [evaluacionesExpandidas, setEvaluacionesExpandidas] = useState({});
  const [filtroGrupoEdad, setFiltroGrupoEdad] = useState('todos');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [ninosFiltrados, setNinosFiltrados] = useState([]);
  const [ninoGrupoSeleccionado, setNinoGrupoSeleccionado] = useState(null);
  const [actualizarEvaluaciones, setActualizarEvaluaciones] = useState(false);
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [evaluacionesFiltradas, setEvaluacionesFiltradas] = useState({});
  const [paginaActual, setPaginaActual] = useState(0);
  const ninosPorPagina = 3;

  const nivelLogroEquivalencia = {
    '1.00': 'No lo consigue',
    '2.00': 'En proceso',
    '3.00': 'Domina el logro'
  };

  const handleDeleteEvaluacion = async (evaluacion) => {
    try {
      // Mostrar toast de proceso
      toast.info('Eliminando evaluación...', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: 'z-50',
        style: {
          fontFamily: '"Exo 2", sans-serif',
          fontWeight: 500
        }
      });

      const response = await deleteEvaluacion(evaluacion.id_evaluacion);

      if (response && response.success) {
        // Mostrar toast de éxito antes de actualizar estados
        toast.success('¡Evaluación eliminada exitosamente!', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: 'z-50',
          style: {
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
          }
        });

        // Esperar un momento para que se muestre el toast
        await new Promise(resolve => setTimeout(resolve, 500));

        // Actualizar estados
        await cargarEvaluaciones();

        if (ninoGrupoSeleccionado) {
          const evaluacionesFiltradas = { ...ninoGrupoSeleccionado.evaluacionesPorFecha };

          Object.keys(evaluacionesFiltradas).forEach(fecha => {
            evaluacionesFiltradas[fecha] = evaluacionesFiltradas[fecha].filter(
              evaluacionItem => evaluacionItem.id_evaluacion !== evaluacion.id_evaluacion
            );

            if (evaluacionesFiltradas[fecha].length === 0) {
              delete evaluacionesFiltradas[fecha];
            }
          });

          setNinoGrupoSeleccionado(prev => ({
            ...prev,
            evaluacionesPorFecha: evaluacionesFiltradas
          }));
        }
      } else {
        toast.error('No se pudo eliminar la evaluación.', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: 'z-50',
          style: {
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar la evaluación.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: 'z-50',
        style: {
          fontFamily: '"Exo 2", sans-serif',
          fontWeight: 500
        }
      });
    }
  };

  const confirmarEliminacion = (evaluacion) => {
    toast.warn(
      <div>
        <p className="font-bold mb-2">¿Está seguro de eliminar esta evaluación?</p>
        <p className="mb-4">Esta acción no se puede deshacer.</p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => toast.dismiss()}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              toast.dismiss(); // Cerrar el diálogo de confirmación
              handleDeleteEvaluacion(evaluacion);
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Eliminar
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        closeButton: false,
        className: "rounded-lg",
      }
    );
  };

  const cargarEvaluaciones = async () => {
    try {
      const response = await obtenerEvaluaciones();
      if (response.success) {
        const evaluacionesAgrupadas = response.data.reduce((acc, evaluacion) => {
          if (!acc[evaluacion.grupo_edad]) {
            acc[evaluacion.grupo_edad] = [];
          }
          acc[evaluacion.grupo_edad].push(evaluacion);
          return acc;
        }, {});
        setEvaluaciones(evaluacionesAgrupadas);

        // Extraer fechas únicas de todas las evaluaciones y ordenarlas correctamente
        // considerando la zona horaria de Ecuador
        const fechasUnicas = [...new Set(response.data.map(evaluacion => {
          // Asegurar que la fecha esté en formato YYYY-MM-DD sin hora
          const fecha = new Date(evaluacion.fecha);
          return formatearFechaLocal(fecha);
        }))]
          .sort((a, b) => {
            // Comparar fechas directamente
            const fechaA = new Date(a);
            const fechaB = new Date(b);
            return fechaB - fechaA; // Orden descendente (más reciente primero)
          });
        setFechasDisponibles(fechasUnicas);
      }
    } catch (error) {
      console.error('Error al cargar evaluaciones:', error);
    }
  };

  useEffect(() => {
    const cargarNinos = async () => {
      try {
        const response = await obtenerTodosLosNinos();
        if (response.success) {
          // Filtrar solo niños con matrícula activa
          const ninosActivos = response.data.filter(nino => nino.estado_matricula === 'Activa');
          setNinos(ninosActivos);
          setNinosFiltrados(ninosActivos);
        }
      } catch (error) {
        console.error('Error al cargar niños:', error);
      }
    };

    cargarNinos();
    cargarEvaluaciones();
  }, []);


  useEffect(() => {
    if (actualizarEvaluaciones) {
      cargarEvaluaciones();
      setActualizarEvaluaciones(false);
    }
  }, [actualizarEvaluaciones]);

  useEffect(() => {
    if (vistaActual === 'recientes') {
      cargarEvaluaciones();
    }
  }, [vistaActual]);

  useEffect(() => {
    const filtrarNinos = () => {
      if (busqueda.trim() === '') {
        setNinosFiltrados([]);
      } else {
        setNinosFiltrados(
          ninos.filter((nino) =>
            `${nino.nombre} ${nino.apellido}`.toLowerCase().includes(busqueda.toLowerCase())
          )
        );
      }
    };

    filtrarNinos();
  }, [busqueda, ninos]);

  // Efecto para actualizar evaluaciones filtradas cuando cambian los filtros
  useEffect(() => {
    if (ninoGrupoSeleccionado) {
      const evaluacionesFiltradas = {};

      // Recorrer todas las evaluaciones del niño
      Object.entries(ninoGrupoSeleccionado.evaluacionesPorFecha).forEach(([fecha, evaluaciones]) => {
        // Si hay un filtro de fecha y la fecha no coincide, omitir
        // Usamos formatearFechaLocal para asegurar que ambas fechas estén en el mismo formato
        const fechaFormateada = formatearFechaLocal(new Date(fecha));
        if (filtroFecha && fechaFormateada !== filtroFecha) {
          return;
        }
        // Mantener todas las evaluaciones de la fecha que cumple el filtro
        evaluacionesFiltradas[fecha] = evaluaciones;
      });

      setEvaluacionesFiltradas(evaluacionesFiltradas);
    }
  }, [filtroFecha, ninoGrupoSeleccionado]);

  const manejarBusqueda = (e) => {
    setBusqueda(e.target.value);
    setPaginaActual(0); // Resetear a la primera página cuando se busca
  };

  const seleccionarNino = async (id) => {
    try {
      const response = await obtenerNinoPorId(id);
      if (response.success) {
        const { fecha_nacimiento } = response.data;
        calcularEdad(fecha_nacimiento);
        setNinoSeleccionado(response.data);
      }
    } catch (error) {
      console.error('Error al seleccionar niño:', error);
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    let diffAnios = hoy.getFullYear() - nacimiento.getFullYear();
    let diffMeses = hoy.getMonth() - nacimiento.getMonth();

    if (hoy.getDate() < nacimiento.getDate()) {
      diffMeses--;
    }

    if (diffMeses < 0) {
      diffAnios--;
      diffMeses += 12;
    }

    let edadCalculada = '';
    if (diffAnios > 0) {
      edadCalculada = `${diffAnios} años`;
    } else {
      edadCalculada = `${Math.max(diffMeses, 0)} meses`;
    }
    setEdad(edadCalculada);
    determinarGrupoEdad(diffAnios, diffMeses);
  };

  const determinarGrupoEdad = (anios, meses) => {
    const mesesTotales = (anios * 12) + meses;

    if (mesesTotales >= 3 && mesesTotales <= 6) {
      setGrupoEdad('3-6 meses');
    } else if (mesesTotales > 6 && mesesTotales <= 9) {
      setGrupoEdad('6-9 meses');
    } else if (mesesTotales > 9 && mesesTotales <= 12) {
      setGrupoEdad('9-12 meses');
    } else if (mesesTotales > 12 && mesesTotales <= 24) {
      setGrupoEdad('12-24 meses');
    } else if (mesesTotales > 24 && mesesTotales <= 36) {
      setGrupoEdad('24-36 meses');
    } else if (mesesTotales > 36 && mesesTotales <= 48) {
      setGrupoEdad('36-48 meses');
    } else {
      setGrupoEdad('Grupo de edad no disponible');
      toast.warning('La edad del niño no corresponde a ningún grupo de evaluación disponible', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: { 
          marginTop: '10px',
          fontFamily: '"Exo 2", sans-serif',
          fontWeight: 500
        }
      });
    }
  };

  const cargarPlantilla = () => {
    if (grupoEdad) {
      setPlantillaCargada(true);
    }
  };


  const toggleEvaluacionExpandida = async (fecha, evalIndex, evaluacion) => {
    const evaluacionId = `${fecha}-${evalIndex}`;

    // Ensure evaluacion exists and has required properties
    if (!evaluacion || !evaluacion.id_evaluacion) {
      console.error('Invalid evaluation object:', evaluacion);
      return;
    }

    try {
      setEvaluacionesExpandidas(prev => ({
        ...prev,
        [evaluacionId]: !prev[evaluacionId] // Toggle the state
      }));

    } catch (error) {
      console.error('Error in toggleEvaluacionExpandida:', error);
      setEvaluacionesExpandidas(prev => ({
        ...prev,
        [evaluacionId]: false
      }));
    }
  };

  const agruparEvaluaciones = (evaluaciones) => {
    return evaluaciones.reduce((grupos, evaluacion) => {
      const key = `${evaluacion.nino.id_child}`;

      if (!grupos[key]) {
        grupos[key] = {
          nino: evaluacion.nino,
          evaluacionesPorFecha: {}
        };
      }

      // Asegurar que la fecha se maneje en la zona horaria de Ecuador
      const fechaEcuador = evaluacion.fecha;
      
      if (!grupos[key].evaluacionesPorFecha[fechaEcuador]) {
        grupos[key].evaluacionesPorFecha[fechaEcuador] = [];
      }

      grupos[key].evaluacionesPorFecha[fechaEcuador].push(evaluacion);
      return grupos;
    }, {});
  };

  const filtrarEvaluaciones = (evaluacionesGrupo) => {
    if (!evaluacionesGrupo) return {};

    const evaluacionesFiltradas = evaluacionesGrupo.filter(evaluacion => {
      const nombreCompleto = `${evaluacion.nino.nombre} ${evaluacion.nino.apellidos}`.toLowerCase();
      const cumpleFiltroNombre = filtroNombre === '' || nombreCompleto.includes(filtroNombre.toLowerCase());
      
      // Formatear la fecha de la evaluación para compararla con el filtro
      // Asegurarse de que la fecha se formatea correctamente para la comparación
      const fechaFormateada = formatearFechaLocal(new Date(evaluacion.fecha));
      const cumpleFiltroFecha = filtroFecha === '' || fechaFormateada === filtroFecha;

      return cumpleFiltroNombre && cumpleFiltroFecha;
    });

    return agruparEvaluaciones(evaluacionesFiltradas);
  };


  const VistaEvaluacionesRecientes = () => {
    // Usar useCallback para memorizar las funciones que manejan los cambios
    const handleFiltroNombreChange = useCallback((e) => {
      setFiltroNombre(e.target.value);
    }, []);
  
    const inputRef = useRef(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const handleFiltroGrupoEdadChange = useCallback((e) => {
      setFiltroGrupoEdad(e.target.value);
    }, []);
  
    const handleFiltroFechaChange = useCallback((e) => {
      setFiltroFecha(e.target.value);
    }, []);
  
    const limpiarFiltros = useCallback(() => {
      setFiltroGrupoEdad('todos');
      setFiltroNombre('');
      setFiltroFecha('');
    }, []);
  
    return (
      <div className="bg-white rounded-lg p-2 sm:p-4 md:p-6 w-full max-w-7xl mx-auto">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6 text-gray-800 border-b pb-2 sm:pb-3">
          Historial de Evaluaciones
        </h2>
  
        {Object.keys(evaluaciones).length > 0 ? (
          <>
            {/* Filtros mejorados */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de búsqueda
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Grupo de Edad
                  </label>
                  <select
                    value={filtroGrupoEdad}
                    onChange={handleFiltroGrupoEdadChange}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="todos">Todos los grupos</option>
                    {Object.keys(evaluaciones).map(grupo => (
                      <option key={grupo} value={grupo}>{grupo}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nombre del Niño
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    value={filtroNombre}
                    onChange={handleFiltroNombreChange}
                    placeholder="Buscar por nombre..."
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Fecha de Evaluación
                  </label>
                  <select
                    value={filtroFecha}
                    onChange={handleFiltroFechaChange}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Todas las fechas</option>
                    {fechasDisponibles.map(fecha => (
                      <option key={fecha} value={fecha}>
                        {fecha}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {(filtroGrupoEdad !== 'todos' || filtroNombre || filtroFecha) && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Filtros activos: {[
                      filtroGrupoEdad !== 'todos' ? `Grupo: ${filtroGrupoEdad}` : null,
                      filtroNombre ? `Nombre: ${filtroNombre}` : null,
                      filtroFecha ? `Fecha: ${filtroFecha}` : null
                    ].filter(Boolean).join(' | ')}
                  </span>
                  <button
                    onClick={limpiarFiltros}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
  
            {/* Vista principal de evaluaciones */}
            {!seccionSeleccionada ? (
              <div className="space-y-6">
                {Object.entries(evaluaciones)
                  .filter(([grupo]) => filtroGrupoEdad === 'todos' || grupo === filtroGrupoEdad)
                  .map(([grupo, evaluacionesGrupo]) => {
                    const evaluacionesAgrupadas = filtrarEvaluaciones(evaluacionesGrupo);
                    if (Object.keys(evaluacionesAgrupadas).length === 0) return null;
  
                    return (
                      <div key={grupo} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Header del grupo */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-800">{grupo}</h3>
                                <p className="text-sm text-gray-600">
                                  {Object.keys(evaluacionesAgrupadas).length} niño(s) evaluado(s)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setSeccionSeleccionada(grupo)}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                              >
                                Ver lista completa
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
  
                        {/* Preview de niños - Ahora clickeables */}
                        <div className="p-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.values(evaluacionesAgrupadas).slice(0, 6).map((ninoData, index) => (
                              <div 
                                key={`${grupo}-${ninoData.nino.nombre}-${index}`}
                                className="bg-gray-50 rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer group border border-transparent hover:border-blue-200"
                                onClick={() => {
                                  setSeccionSeleccionada(grupo);
                                  setNinoGrupoSeleccionado(ninoData);
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gray-200 group-hover:bg-blue-200 rounded-full flex items-center justify-center transition-colors">
                                    <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 truncate transition-colors">
                                      {`${ninoData.nino.nombre} ${ninoData.nino.apellidos}`}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {Object.values(ninoData.evaluacionesPorFecha).flat().length} evaluación(es)
                                    </p>
                                  </div>
                                  <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            ))}
                            {Object.keys(evaluacionesAgrupadas).length > 6 && (
                              <div 
                                className="bg-gray-50 rounded-lg p-4 flex items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                                onClick={() => setSeccionSeleccionada(grupo)}
                              >
                                <p className="text-sm text-gray-600 hover:text-blue-600 transition-colors">
                                  +{Object.keys(evaluacionesAgrupadas).length - 6} niño(s) más
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              /* Vista detallada del grupo seleccionado */
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setSeccionSeleccionada(null);
                      setNinoGrupoSeleccionado(null);
                    }}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    Volver a grupos
                  </button>
                  <h3 className="text-2xl font-bold text-gray-800">
                    Grupo: {seccionSeleccionada}
                  </h3>
                </div>
  
                {!ninoGrupoSeleccionado ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.values(filtrarEvaluaciones(evaluaciones[seccionSeleccionada] || [])).map((ninoData, index) => (
                      <div
                        key={`detalle-${seccionSeleccionada}-${ninoData.nino.nombre}-${index}`}
                        className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
                        onClick={() => setNinoGrupoSeleccionado(ninoData)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                                {`${ninoData.nino.nombre} ${ninoData.nino.apellidos}`}
                              </h4>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
  
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Total de evaluaciones:</span>
                            <span className="font-semibold text-gray-800">
                              {Object.values(ninoData.evaluacionesPorFecha).flat().length}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Última evaluación:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {(() => {
                                const fechaStr = Object.keys(ninoData.evaluacionesPorFecha)
                                  .sort((a, b) => {
                                    // Usar formatearFechaLocal para obtener fechas en zona horaria correcta
                                    const fechaA = new Date(a);
                                    const fechaB = new Date(b);
                                    return fechaB - fechaA;
                                  })[0];
                                // Mostrar la fecha en formato YYYY-MM-DD sin hora
                                return formatearFechaLocal(new Date(fechaStr));
                              })()}
                            </span>
                          </div>
                          <div className="pt-2 border-t border-gray-100">
                            <span className="text-xs text-blue-600 font-medium">
                              Click para ver historial completo →
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Vista detallada del niño seleccionado */
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => setNinoGrupoSeleccionado(null)}
                        className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <ChevronLeft className="w-5 h-5 mr-1" />
                        Volver a lista
                      </button>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h4 className="text-2xl font-bold text-gray-800">
                          {`${ninoGrupoSeleccionado.nino.nombre} ${ninoGrupoSeleccionado.nino.apellidos}`}
                        </h4>
                      </div>
                    </div>
  
                    {/* Timeline de evaluaciones */}
                    <div className="space-y-4">
                      {Object.entries(filtroFecha ? evaluacionesFiltradas : ninoGrupoSeleccionado.evaluacionesPorFecha)
                        .sort(([a], [b]) => {
                          const fechaA = new Date(a);
                          const fechaB = new Date(b);
                          return fechaB - fechaA;
                        })
                        .map(([fecha, evaluaciones], fechaIndex) => (
                          <div key={fechaIndex} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            {/* Header de fecha */}
                            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <div>
                                  <h5 className="text-lg font-bold text-gray-800">
                                    {formatearFechaLocal(new Date(fecha))}
                                  </h5>
                                  <p className="text-sm text-gray-600">
                                    {evaluaciones.length} evaluación(es) registrada(s)
                                  </p>
                                </div>
                              </div>
                            </div>
  
                            {/* Lista de evaluaciones */}
                            <div className="divide-y divide-gray-100">
                              {evaluaciones.map((evaluacion, evalIndex) => {
                                const evaluacionId = `${fecha}-${evalIndex}`;
                                const isExpanded = evaluacionesExpandidas[evaluacionId];
                                
                                return (
                                  <div key={evalIndex} className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                        </div>
                                        <div>
                                          <h6 className="text-lg font-semibold text-gray-800">
                                            Evaluación #{evalIndex + 1}
                                          </h6>
                                          <p className="text-sm text-gray-600">
                                            {evaluacion.ambitos.length} indicadores evaluados
                                          </p>
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleEvaluacionExpandida(fecha, evalIndex, evaluacion);
                                          }}
                                          className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                          {isExpanded ? (
                                            <>
                                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                              </svg>
                                              Ocultar
                                            </>
                                          ) : (
                                            <>
                                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                              </svg>
                                              Ver detalles
                                            </>
                                          )}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmarEliminacion(evaluacion);
                                          }}
                                          className="flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4 mr-1" />
                                          Eliminar
                                        </button>
                                      </div>
                                    </div>
  
                                    {isExpanded && (
                                      <div className="mt-6 space-y-6">
                                        {/* Leyenda de niveles */}
                                        <div className="bg-gray-50 rounded-lg p-4">
                                          <h6 className="text-sm font-semibold text-gray-700 mb-3">Niveles de logro:</h6>
                                          <div className="flex flex-wrap gap-3">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                              <span className="text-sm text-gray-700">No lo consigue</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                              <span className="text-sm text-gray-700">En proceso</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                              <span className="text-sm text-gray-700">Domina el logro</span>
                                            </div>
                                          </div>
                                        </div>
  
                                        {/* Resultados por ámbito */}
                                        {Object.entries(
                                          evaluacion.ambitos.reduce((grupos, ambito) => {
                                            if (!grupos[ambito.ambito_aprendizaje]) {
                                              grupos[ambito.ambito_aprendizaje] = [];
                                            }
                                            grupos[ambito.ambito_aprendizaje].push(ambito);
                                            return grupos;
                                          }, {})
                                        ).map(([nombreAmbito, ambitosGrupo], index) => (
                                          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
                                              <h4 className="text-blue-800 font-semibold flex items-center">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                </svg>
                                                {nombreAmbito}
                                              </h4>
                                            </div>
                                            <div className="p-4 space-y-3">
                                              {ambitosGrupo.map((ambito, ambitoIndex) => (
                                                <div key={ambitoIndex} className="flex items-start space-x-4 p-3 bg-white rounded-lg border border-gray-100">
                                                  <div className="flex-1">
                                                    <p className="text-gray-700 text-sm leading-relaxed">
                                                      {ambito.indicador}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center space-x-3">
                                                    <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium
                                                      ${ambito.nivel_logro === '3' ? 'bg-green-100 text-green-800' :
                                                        ambito.nivel_logro === '2' ? 'bg-yellow-100 text-yellow-800' :
                                                          'bg-red-100 text-red-800'}`}
                                                    >
                                                      <div className={`w-2 h-2 rounded-full mr-2
                                                        ${ambito.nivel_logro === '3' ? 'bg-green-500' :
                                                          ambito.nivel_logro === '2' ? 'bg-yellow-500' :
                                                            'bg-red-500'}`}
                                                      >
                                                      </div>
                                                      Nivel {ambito.nivel_logro}
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Estado vacío */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay evaluaciones registradas</h3>
                <p className="text-gray-500 mb-1">Aún no se han realizado evaluaciones</p>
                <p className="text-sm text-gray-400">Comience creando una nueva evaluación</p>
              </div>
              <button
                onClick={() => setVistaActual('nueva')}
                className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Crear Primera Evaluación
              </button>
            </div>
          </div>
        )}
        
        <ToastContainer />
      </div>
    );
  };

  const VistaNuevaEvaluacion = () => (
    <div className="bg-white rounded-lg shadow-lg p-2 sm:p-4 md:p-6 w-full max-w-7xl mx-auto">
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-black mb-3 sm:mb-4">Nueva Evaluación</h2>
      
      {/* Buscador */}
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar niño por nombre o apellido..."
            value={busqueda}
            onChange={manejarBusqueda}
            className="w-full pl-10 pr-4 py-3 border bg-white focus:text-black rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            autoFocus
          />
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
        {busqueda && (
          <div className="mt-2 text-sm text-gray-500">
            {ninosFiltrados.length === 0 ? (
              <p>No se encontraron resultados para "{busqueda}"</p>
            ) : (
              <p>Se encontraron {ninosFiltrados.length} resultado(s)</p>
            )}
          </div>
        )}
      </div>

      {/* Lista de niños */}
      <div className="mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <User className="h-5 w-5 mr-2 text-blue-600" />
          {busqueda ? 'Resultados de búsqueda' : 'Lista de niños disponibles'}
        </h3>
        
        {/* Mostrar resultados filtrados o lista completa */}
         {busqueda ? (
           ninosFiltrados.length > 0 ? (
             <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
               <div>
                 {/* Carrusel de resultados de búsqueda */}
                 <div className="relative">
                   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 min-h-[80px]">
                     {ninosFiltrados
                       .slice(paginaActual * ninosPorPagina, (paginaActual + 1) * ninosPorPagina)
                       .map((nino) => (
                         <div
                           key={nino.id_child}
                           className="bg-white p-2 rounded-lg border border-blue-100 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200 group"
                           onClick={() => seleccionarNino(nino.id_child)}
                         >
                           <div className="flex items-center space-x-2">
                             <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                               <User className="h-4 w-4 text-blue-600" />
                             </div>
                             <div className="flex-1 min-w-0">
                               <p className="text-sm font-medium text-gray-900 truncate">
                                 {nino.nombre} {nino.apellido}
                               </p>
                               <p className="text-xs text-gray-500">Clic para seleccionar</p>
                             </div>
                           </div>
                         </div>
                       ))
                     }
                   </div>
                 </div>
                 
                 {/* Controles de paginación para búsqueda */}
                 {ninosFiltrados.length > ninosPorPagina && (
                   <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-200">
                     <div className="flex items-center space-x-2">
                       <button
                         onClick={() => setPaginaActual(Math.max(0, paginaActual - 1))}
                         disabled={paginaActual === 0}
                         className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                         </svg>
                         Anterior
                       </button>
                       
                       <button
                         onClick={() => setPaginaActual(Math.min(Math.ceil(ninosFiltrados.length / ninosPorPagina) - 1, paginaActual + 1))}
                         disabled={paginaActual >= Math.ceil(ninosFiltrados.length / ninosPorPagina) - 1}
                         className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         Siguiente
                         <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                         </svg>
                       </button>
                     </div>
                     
                     {/* Indicador de página para búsqueda */}
                     <div className="flex items-center space-x-1">
                       <span className="text-sm text-blue-700">
                         Página {paginaActual + 1} de {Math.ceil(ninosFiltrados.length / ninosPorPagina)}
                       </span>
                       <span className="text-xs text-blue-500">({ninosFiltrados.length} resultados)</span>
                     </div>
                     
                     {/* Indicadores de puntos para búsqueda */}
                     <div className="flex space-x-1">
                       {Array.from({ length: Math.ceil(ninosFiltrados.length / ninosPorPagina) }, (_, index) => (
                         <button
                           key={index}
                           onClick={() => setPaginaActual(index)}
                           className={`w-2 h-2 rounded-full transition-colors ${
                             index === paginaActual ? 'bg-blue-600' : 'bg-blue-300 hover:bg-blue-400'
                           }`}
                         />
                       ))}
                     </div>
                   </div>
                 )}
               </div>
             </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No se encontraron niños</p>
              <p className="text-gray-400 text-sm">Intenta con un nombre diferente</p>
            </div>
          )
        ) : (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            {ninos.length > 0 ? (
              <div>
                {/* Carrusel de niños */}
                <div className="relative">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 min-h-[80px]">
                    {ninos
                      .slice(paginaActual * ninosPorPagina, (paginaActual + 1) * ninosPorPagina)
                      .map((nino) => (
                        <div
                          key={nino.id_child}
                          className="bg-white p-2 rounded-lg border border-gray-100 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200 group"
                          onClick={() => seleccionarNino(nino.id_child)}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                              <User className="h-4 w-4 text-gray-600 group-hover:text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {nino.nombre} {nino.apellido}
                              </p>
                              <p className="text-xs text-gray-500">Clic para seleccionar</p>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
                
                {/* Controles de paginación */}
                {ninos.length > ninosPorPagina && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPaginaActual(Math.max(0, paginaActual - 1))}
                        disabled={paginaActual === 0}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Anterior
                      </button>
                      
                      <button
                        onClick={() => setPaginaActual(Math.min(Math.ceil(ninos.length / ninosPorPagina) - 1, paginaActual + 1))}
                        disabled={paginaActual >= Math.ceil(ninos.length / ninosPorPagina) - 1}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Siguiente
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Indicador de página */}
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-gray-500">
                        Página {paginaActual + 1} de {Math.ceil(ninos.length / ninosPorPagina)}
                      </span>
                      <span className="text-xs text-gray-400">({ninos.length} niños total)</span>
                    </div>
                    
                    {/* Indicadores de puntos */}
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.ceil(ninos.length / ninosPorPagina) }, (_, index) => (
                        <button
                          key={index}
                          onClick={() => setPaginaActual(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === paginaActual ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No hay niños registrados</p>
                <p className="text-gray-400 text-sm">Contacta al administrador para registrar niños</p>
              </div>
            )}
          </div>
        )}
      </div>

      {ninoSeleccionado && (
        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 md:p-4 mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-bold mb-2 text-black">Información del Niño</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm md:text-base">
            <p className='text-black'>
              <span className="font-semibold">Nombre:</span>{' '}
              {`${ninoSeleccionado.nombre} ${ninoSeleccionado.apellidos}`}
            </p>
            <p className='text-black'>
              <span className="font-semibold">Edad:</span> {edad}
            </p>
            <p className='text-black'>
              <span className="font-semibold">Grupo de Edad:</span> {grupoEdad}
            </p>
          </div>
          <button
            onClick={cargarPlantilla}
            className="mt-3 sm:mt-4 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm md:text-base w-full sm:w-auto"
          >
            Cargar Plantilla de Evaluación
          </button>
        </div>
      )}

      {plantillaCargada && (
        <div className="bg-white rounded-lg">
          <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-black">Plantilla de Evaluación</h3>
          <PlantillaEvaluacion
            grupoEdad={grupoEdad}
            nombre={ninoSeleccionado.nombre}
            apellidos={ninoSeleccionado.apellidos}
            fechaNacimiento={ninoSeleccionado.fecha_nacimiento}
            idChild={ninoSeleccionado.id_child}
            onEvaluacionGuardada={() => {
              setActualizarEvaluaciones(true);
              setPlantillaCargada(false);
              setNinoSeleccionado(null);
              setBusqueda('');
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white rounded-2xl p-2 sm:p-4 shadow-lg">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 md:mb-6 space-y-2 sm:space-y-0 p-4">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Evaluaciones</h1>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-4">
            <button
              onClick={() => {
                setVistaActual('recientes');
                setSeccionSeleccionada(null);
                setNinoGrupoSeleccionado(null);
                cargarEvaluaciones();
              }}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm md:text-base w-full sm:w-auto
                ${vistaActual === 'recientes'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Evaluaciones Recientes
            </button>
            <button
              onClick={() => setVistaActual('nueva')}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm md:text-base w-full sm:w-auto
                ${vistaActual === 'nueva'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Nueva Evaluación
            </button>
          </div>
        </div>

        {vistaActual === 'recientes' ? (
          <VistaEvaluacionesRecientes />
        ) : (
          <VistaNuevaEvaluacion />
        )}
      </div>
    </div>
  );
};

export default GestionEvaluaciones;
